import cloneDeep from 'lodash/cloneDeep';
import isFinite from 'lodash/isFinite';
import isUndefined from 'lodash/isUndefined';
import filter from 'lodash/filter';
import map from 'lodash/map';

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  appendReferenceLine,
  removeReferenceLine,
  setReferenceLineColor,
  setReferenceLineLabel,
  setReferenceLineValue,
  setOrderBy,
} from 'common/authoring_workflow/actions';
import {
  getDimension,
  getReferenceLines,
  getOrderBy,
  isBarChart,
  isColumnChart,
  isComboChart,
  isHistogram,
  isOneHundredPercentStacked,
  isTimelineChart,
  isPieChart,
} from 'common/authoring_workflow/selectors/vifAuthoring';
import { getCurrentMetadata, isDimensionTypeCalendarDate } from '../../../selectors/metadata';
import { DualAxisOptions } from './DualAxisOptions';
import { AxisPrecisionSelectorSingle } from './AxisPrecisionSelectorSingle';
import { AxisPrecisionSelectorDual } from './AxisPrecisionSelectorDual';
import { AxisScaleSelectorDual } from './AxisScaleSelectorDual';
import { AxisScaleSelectorSingle } from './AxisScaleSelectorSingle';
import { LogarithmicScaleSelector } from './LogarithmicScaleSelector';
import DebouncedInput from '../../shared/DebouncedInput';
import TextInputButton from '../../shared/TextInputButton';
import EmptyPane from '../EmptyPane';
import {
  AccordionContainerV2 as AccordionContainer,
  AccordionPaneV2 as AccordionPane,
} from 'common/components/Accordion';
import ColorPicker from 'common/components/ColorPicker';
import Dropdown from 'common/components/Dropdown';
import { I18n } from 'common/i18n';
import { formatString } from 'common/js_utils/formatString';
import { CHART_SORTING, COLORS } from 'common/authoring_workflow/constants';

export const AxisPane = () => {
  const dispatch = useDispatch();
  const vifAuthoring = useSelector((state: any) => state.vifAuthoring);
  const metadata = useSelector((state: any) =>
    getCurrentMetadata(state.metadataCollection, state.vifAuthoring),
  );
  const chartSorting = cloneDeep(CHART_SORTING);

  const renderChartSortingOption = (option: any) => (
    <div>
      <span className={option.icon}></span> {option.title}
    </div>
  );

  const renderChartSorting = () => {
    const defaultChartSort = getOrderBy(vifAuthoring) || { parameter: 'measure', sort: 'desc' };
    const options = map(chartSorting, (option: any) => {
      option.value = `${option.orderBy.parameter}-${option.orderBy.sort}`;
      option.render = renderChartSortingOption;
      return option;
    });

    return (
      <AccordionPane title={I18n.t('shared.visualizations.panes.axis.subheaders.chart_sorting')}>
        <div className="authoring-field">
          <Dropdown
            id="chart-sorting-selection"
            options={options}
            onSelection={(option: any) => dispatch(setOrderBy(option.orderBy))}
            value={`${defaultChartSort.parameter}-${defaultChartSort.sort}`}
          />
        </div>
      </AccordionPane>
    );
  };

  const renderAxisScaleSelector = () => {
    const axisScaleSelector = isComboChart(vifAuthoring) ? (
      <AxisScaleSelectorDual />
    ) : (
      <AxisScaleSelectorSingle />
    );
    const axisPrecisionSelector = isComboChart(vifAuthoring) ? (
      <AxisPrecisionSelectorDual />
    ) : (
      <AxisPrecisionSelectorSingle />
    );
    const logarithmicScaleSelector = isTimelineChart(vifAuthoring) ? <LogarithmicScaleSelector /> : null;
    return (
      <AccordionPane title={I18n.t('shared.visualizations.panes.axis.subheaders.scale')}>
        <div className="measure-axis-container">
          <label className="measure-axis-bounds-label">
            {I18n.t('shared.visualizations.panes.axis.fields.scale.title')}
          </label>
          {axisScaleSelector}
          {logarithmicScaleSelector}
        </div>
        <div className="measure-precision-container">
          <label className="measure-axis-precision-label">
            {I18n.t('shared.visualizations.panes.axis.fields.precision.title')}
          </label>
          {axisPrecisionSelector}
        </div>
      </AccordionPane>
    );
  };

  const renderDualAxisControls = () => (
    <AccordionPane
      title={I18n.t('shared.visualizations.panes.dual_axis_options.subheaders.dual_axis_options')}
    >
      <DualAxisOptions />
    </AccordionPane>
  );

  const renderReferenceLinesControls = () => {
    const controls = getReferenceLines(vifAuthoring).map(renderReferenceLinesControlsAtIndex);
    const link = renderAddReferenceLineLink();
    return (
      <AccordionPane title={I18n.t('shared.visualizations.panes.reference_lines.subheaders.reference_lines')}>
        {controls}
        {link}
      </AccordionPane>
    );
  };

  const renderReferenceLinesControlsAtIndex = (referenceLine: any, referenceLineIndex: number) => {
    const headerLabel = formatString(
      I18n.t('shared.visualizations.panes.reference_lines.fields.reference_line_placeholder'),
      referenceLineIndex + 1,
    );
    const valueInputAttributes: any = {
      className: 'text-input',
      id: `reference-lines-value-input-${referenceLineIndex}`,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        const i = parseFloat(event.target.value);
        const value = isNaN(i) ? null : i;
        dispatch(setReferenceLineValue({ referenceLineIndex, value }));
      },
      placeholder: I18n.t('shared.visualizations.panes.reference_lines.fields.add_value'),
      step: 1,
      type: 'number',
      value: isFinite(referenceLine.value) ? referenceLine.value.toString() : '',
    };
    if (isOneHundredPercentStacked(vifAuthoring)) {
      valueInputAttributes.max = 100;
      valueInputAttributes.min = -100;
    }
    const textInputButton = renderReferenceLinesLabelTextInputButton(referenceLine, referenceLineIndex);

    return (
      <div
        className="reference-lines-reference-line-container"
        id={`reference-lines-reference-line-container-${referenceLineIndex}`}
        key={referenceLine.uId}
      >
        <label className="block-label">{headerLabel}</label>
        <div className="reference-lines-controls-container">
          <DebouncedInput {...valueInputAttributes} />
          {textInputButton}
          <ColorPicker
            id="axis-pane-color-picker"
            handleColorChange={(color: string) =>
              dispatch(setReferenceLineColor({ referenceLineIndex, color }))
            }
            palette={COLORS}
            value={referenceLine.color}
          />
          <a onClick={() => dispatch(removeReferenceLine(referenceLineIndex))}>
            <span className="socrata-icon-close" />
          </a>
        </div>
      </div>
    );
  };

  const renderReferenceLinesLabelTextInputButton = (referenceLine: any, referenceLineIndex: number) => {
    return (
      <TextInputButton
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(
            setReferenceLineLabel({
              referenceLineIndex,
              label: event.target.value,
            }),
          );
        }}
        placeholder={I18n.t('shared.visualizations.panes.reference_lines.fields.add_label')}
        textInputId={`reference-lines-label-input-${referenceLineIndex}`}
        textInputValue={referenceLine.label}
      />
    );
  };

  const renderAddReferenceLineLink = () => {
    const lines = getReferenceLines(vifAuthoring);
    const linesWithoutValues = filter(lines, (line) => isUndefined(line.value));
    const isDisabled = linesWithoutValues.length > 0;
    return (
      <div className="reference-lines-add-reference-line-link-container">
        <a
          className={isDisabled ? 'disabled' : undefined}
          id={'reference-lines-add-reference-line-link'}
          onClick={isDisabled ? undefined : () => dispatch(appendReferenceLine())}
        >
          <span className="socrata-icon-add" />
          {I18n.t('shared.visualizations.panes.reference_lines.fields.add_reference_line')}
        </a>
      </div>
    );
  };

  const renderBarChartControls = () => (
    <AccordionContainer>
      {renderAxisScaleSelector()}
      {renderChartSorting()}
      {renderReferenceLinesControls()}
    </AccordionContainer>
  );

  const renderColumnChartControls = () => (
    <AccordionContainer>
      {renderAxisScaleSelector()}
      {renderChartSorting()}
      {renderReferenceLinesControls()}
    </AccordionContainer>
  );

  const renderComboChartControls = () => (
    <AccordionContainer>
      {renderDualAxisControls()}
      {renderAxisScaleSelector()}
      {renderChartSorting()}
      {renderReferenceLinesControls()}
    </AccordionContainer>
  );

  const renderHistogramControls = () => (
    <AccordionContainer>
      {renderAxisScaleSelector()}
      {renderReferenceLinesControls()}
    </AccordionContainer>
  );

  const renderTimelineChartControls = () => {
    const dimension = getDimension(vifAuthoring);
    const chartSortingControl = !isDimensionTypeCalendarDate(metadata, dimension)
      ? renderChartSorting()
      : null;
    return (
      <AccordionContainer>
        {renderAxisScaleSelector()}
        {chartSortingControl}
        {renderReferenceLinesControls()}
      </AccordionContainer>
    );
  };

  const renderPieChartControls = () => <AccordionContainer>{renderChartSorting()}</AccordionContainer>;

  const renderEmptyPane = () => <EmptyPane />;

  let configuration;
  if (isBarChart(vifAuthoring)) {
    configuration = renderBarChartControls();
  } else if (isColumnChart(vifAuthoring)) {
    configuration = renderColumnChartControls();
  } else if (isComboChart(vifAuthoring)) {
    configuration = renderComboChartControls();
  } else if (isHistogram(vifAuthoring)) {
    configuration = renderHistogramControls();
  } else if (isTimelineChart(vifAuthoring)) {
    configuration = renderTimelineChartControls();
  } else if (isPieChart(vifAuthoring)) {
    configuration = renderPieChartControls();
  } else {
    configuration = renderEmptyPane();
  }

  return <form>{configuration}</form>;
};
